// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { getVehicleTransfersAPI } from "services/api/fleet/vehicles/locations/search/api";
import { initialVehicleTransferSearchForm } from "services/api/fleet/vehicles/locations/search/consts";
import {
  VehicleTransferRow,
  VehicleTransferSearchFormValues,
} from "services/api/fleet/vehicles/locations/search/types";

// Hook-ovi
import useUrlFilters from "services/hooks/useUrlFilters";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { FLEET_TRANSFER_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalna navigacija
import { Routes } from "ui/modules/fleet/transfers/navigation";

// Lokalne komponente
import TransferSearchFilters from "./components/TransferSearchFilters/TransferSearchFilters";
import TransferSearchResults from "./components/TransferSearchResults/TransferSearchResults";
import { TransferSearchPageCmp, TransferSearchPageProps } from "./types";

/**
 * Premještaji kroz sva vozila flote — gdje je koje vozilo otišlo, kada i tko ga je vozio.
 * Povijest jednog vozila je na kartici vozila, u tabu "Premještaji".
 */
const TransferSearchPage: TransferSearchPageCmp = (
  _props: TransferSearchPageProps
) => {
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleTransferRow>
  >(initialDataWithPagination);
  // Vrsta lokacije i datumski raspon idu u URL — drill-down s drugih ekrana i dijeljenje linka.
  const [filterValues, setFilterValues] =
    useUrlFilters<VehicleTransferSearchFormValues>(
      initialVehicleTransferSearchForm,
      {
        locationType: { param: "locationType" },
        validFromFrom: { param: "validFromFrom", type: "date" },
        validFromTo: { param: "validFromTo", type: "date-end" },
      }
    );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: VehicleTransferSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      setSearchResults(await getVehicleTransfersAPI(pagination, values));
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    // Kreni s vrijednostima iz URL-a (useUrlFilters ih je već popunio).
    fetchSearchResult(initialDataWithPagination.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (values: VehicleTransferSearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchSearchResult(pagination, filterValues);
    },
    [filterValues]
  );

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_TRANSFER_SEARCH_PAGE_TITLE.text}
              icon={FLEET_TRANSFER_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_TRANSFER_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <TransferSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema premještaja" />
          ) : (
            <TransferSearchResults
              searchResults={searchResults}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onVehicleClick={handleVehicleClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

TransferSearchPage.route = Routes.TransferSearchPageRouter;

export default TransferSearchPage;
